/**
 * @file Has the host confirmed their time zone? (host-availability-ui-design
 * §3.11, U18; implementation plan FE-2.4.)
 *
 * Drives the get-ready nudge between P2 and P3b: the sidebar's "Set up" pill
 * on Calendar, and the `BookingReadyCard` at the top of the Inbox and the
 * Packages hub. Both show only while the server says `settings.saved` is
 * false; until it has said anything they stay hidden, so a slow or failed
 * read never flashes a nudge the host doesn't need.
 *
 * The answer lives in `hostInboxSlice.availabilitySaved`: read once a session
 * (`fetchAvailabilitySetup`, the current month's `GET host/calendar`) for an
 * approved host, and rewritten by the calendar page from every read and after
 * a zone save, so the pill and the card go as soon as the save lands.
 *
 * Plain .js: no JSX.
 */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchAvailabilitySetup, selectAvailabilitySaved } from 'store/features/hostInboxSlice';
import { HOST_USER_TYPE } from 'utils/host-area-access';

/** The calendar exists for approved hosts only, as the inbox does (T11). */
const selectHasCalendar = (state) =>
  Boolean(state.user?.isLoggedIn && state.user?.data?.userType === HOST_USER_TYPE && state.user?.data?.isApproved);

/**
 * @returns {{ saved: boolean|null, needsSetup: boolean }} `needsSetup` is true
 *   only when the server has said the zone isn't saved.
 */
export const useAvailabilitySetup = () => {
  const dispatch = useDispatch();
  const hasCalendar = useSelector(selectHasCalendar);
  const saved = useSelector(selectAvailabilitySaved);

  useEffect(() => {
    if (hasCalendar && saved === null) dispatch(fetchAvailabilitySetup());
  }, [dispatch, hasCalendar, saved]);

  return { saved, needsSetup: hasCalendar && saved === false };
};

export default useAvailabilitySetup;
